import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function SpreadsheetArticle() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showTOC, setShowTOC] = useState(false);

  const tocItems = [
    { id: 'what-is', label: t('what_is_spreadsheet') },
    { id: 'how-it-works', label: t('how_it_works') },
    { id: 'how-to-find', label: t('how_to_find') },
    { id: 'categories', label: t('product_categories') },
    { id: 'seller-research', label: t('seller_research') },
    { id: 'qc', label: t('qc_guide') },
    { id: 'shipping', label: t('shipping_cost') },
    { id: 'safety', label: t('safety') },
    { id: 'comparison', label: t('comparison') },
    { id: 'faq', label: t('faq') },
  ];

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setShowTOC(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <article className="pt-24 pb-16">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-500 mb-6 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              {t('back_to_home')}
            </button>

            <div className="mb-8">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-800 text-xs font-medium rounded-full mb-4">
                {t('article_category_spreadsheet')}
              </span>
              <h1 className="text-3xl md:text-5xl font-heading font-semibold text-foreground-950 mb-4 leading-tight">
                CSSBuy Spreadsheet 2026 — Complete Product & Buying Guide
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span>{t('published_date')}: 2026-08-15</span>
                <span>{t('updated_date')}: 2026-08-27</span>
                <span>8 min read</span>
              </div>
            </div>

            <div className="aspect-[21/9] rounded-xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Minimalist%20overhead%20view%20of%20organized%20fashion%20products%20grid%20arrangement%20soft%20neutral%20linen%20background%20warm%20natural%20light%20editorial%20photography%20clean%20aesthetic%20product%20spreadsheet%20concept%20premium%20quality&width=1200&height=500&seq=article-hero-spreadsheet&orientation=landscape"
                alt="CSSBuy Spreadsheet Guide"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex flex-col lg:flex-row gap-10">
              <div className="lg:w-64 flex-shrink-0">
                <div className="lg:sticky lg:top-28">
                  <button
                    onClick={() => setShowTOC(!showTOC)}
                    className="lg:hidden w-full flex items-center justify-between p-4 bg-white rounded-lg border border-background-200 mb-4"
                  >
                    <span className="text-sm font-medium">{t('table_of_contents')}</span>
                    <i className={`ri-arrow-down-s-line ${showTOC ? 'rotate-180' : ''}`} />
                  </button>

                  <div className={`${showTOC ? 'block' : 'hidden'} lg:block`}>
                    <div className="bg-white rounded-lg border border-background-200 p-5">
                      <h3 className="text-sm font-semibold text-foreground-900 uppercase tracking-wider mb-4">
                        {t('table_of_contents')}
                      </h3>
                      <nav className="space-y-2">
                        {tocItems.map((item) => (
                          <button
                            key={item.id}
                            onClick={() => scrollTo(item.id)}
                            className="block text-sm text-foreground-600 hover:text-primary-600 transition-colors text-left w-full"
                          >
                            {item.label}
                          </button>
                        ))}
                      </nav>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed space-y-8">
                  <section id="what-is">
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                      {t('what_is_spreadsheet')}
                    </h2>
                    <p>
                      CSSBuy Spreadsheet is an independent product discovery and comparison resource designed to help buyers navigate the complex landscape of international purchasing. Unlike generic marketplaces, a CSSBuy Spreadsheet organizes product information into structured categories, making it easy to compare sellers, prices, shipping options, and quality control standards in one place.
                    </p>
                    <p>
                      In 2026, the CSSBuy Spreadsheet has become one of the most trusted tools for buyers looking to source products from overseas. The spreadsheet covers over 14 major categories including Shoes, Hoodies, T-Shirts, Jackets, Pants, Headwear, Accessories, Bags, and more. Each category is regularly updated with new finds, verified sellers, and current pricing information.
                    </p>
                    <p>
                      Whether you are a first-time buyer or an experienced shopper, the CSSBuy Spreadsheet provides the structured data you need to make informed decisions. From checking QC photos before shipping to comparing shipping methods for your country, the spreadsheet serves as a comprehensive buying companion.
                    </p>
                  </section>

                  <section id="how-it-works">
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                      {t('how_it_works')}
                    </h2>
                    <p>
                      The CSSBuy Spreadsheet works by aggregating product information from multiple sources and presenting it in a clean, organized format. Users can browse by category, search for specific items, or filter by criteria such as price range, seller reputation, and available QC photos.
                    </p>
                    <p>
                      When you find a product you are interested in, you can click through to view more details on the main website. The spreadsheet provides direct links to product pages, saving you time and reducing the risk of searching through unreliable listings. Each entry typically includes the product name, category, approximate price range, seller information, and whether quality control photos are available.
                    </p>
                    <p>
                      One of the key features of the CSSBuy Spreadsheet is its regular update cycle. The team behind the resource constantly monitors new releases, seller changes, and price adjustments to ensure the information remains accurate and useful. This makes it a reliable starting point for anyone planning to use CSSBuy as their purchasing agent.
                    </p>
                  </section>

                  <section id="how-to-find">
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                      {t('how_to_find')}
                    </h2>
                    <p>
                      Finding products on the CSSBuy Spreadsheet is straightforward. You can use the category dropdown menu to browse specific product types, or use the search bar to look for items by name, brand, or keyword. The search function supports partial matches, making it easy to find products even if you only know part of the name.
                    </p>
                    <p>
                      For best results, use specific keywords related to the product you are looking for. For example, instead of searching for "shoes," try searching for "Nike Dunk" or "Jordan 1" to narrow down the results. The spreadsheet also supports filtering by price range, which helps you find products that fit your budget.
                    </p>
                    <p>
                      Another useful method is to browse the "Popular Finds" section, which highlights trending items and best sellers. This section is updated weekly and reflects the current buying trends in the community. It is a great way to discover new products that you might not have been looking for initially.
                    </p>
                  </section>

                  <section id="categories">
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                      {t('product_categories')}
                    </h2>
                    <p>
                      The CSSBuy Spreadsheet organizes products into clear categories to make browsing easier. The main categories include:
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 my-4">
                      {['Shoes', 'Hoodies/Sweaters', 'T-Shirts', 'Jackets', 'Pants/Shorts', 'Headwear', 'Sets', 'Underwear', 'Jersey', 'Accessories', 'Bags', 'Belts', 'Perfumes', 'Other Stuff'].map((cat) => (
                        <a
                          key={cat}
                          href="https://kaspreadsheet.com"
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-2 p-3 bg-background-100 rounded-lg hover:bg-primary-50 hover:text-primary-600 transition-colors text-sm"
                        >
                          <i className="ri-arrow-right-circle-line text-primary-400" />
                          {cat}
                        </a>
                      ))}
                    </div>
                    <p>
                      Each category is maintained with fresh listings and updated information. Clicking any category will take you to the corresponding page on the main website where you can browse the full product range.
                    </p>
                  </section>

                  <section id="seller-research">
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                      {t('seller_research')}
                    </h2>
                    <p>
                      Researching sellers is a critical part of the buying process when using CSSBuy. The spreadsheet includes seller information where available, but it is always recommended to do additional research before placing an order. Look for sellers with a history of positive reviews, clear product photos, and responsive customer service.
                    </p>
                    <p>
                      Some sellers specialize in specific product categories, which often means better quality and more accurate descriptions. For example, certain sellers may be known for high-quality sneakers, while others excel at streetwear or accessories. The community often shares seller recommendations in forums and social media groups, which can be a valuable source of information.
                    </p>
                    <p>
                      Always verify seller information on the platform directly before making a purchase. Do not rely solely on third-party information, as seller status and product availability can change quickly.
                    </p>
                  </section>

                  <section id="qc">
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                      {t('qc_guide')}
                    </h2>
                    <p>
                      Quality Control (QC) is a service where your items are inspected and photographed before they are shipped from the warehouse. CSSBuy offers QC photos as part of their service, allowing you to verify that the products you receive match the descriptions and photos from the seller.
                    </p>
                    <p>
                      When you receive QC photos, pay attention to details such as stitching, material quality, color accuracy, and any defects. If something looks wrong, you can request additional photos or initiate a return. The QC process is your best protection against receiving low-quality or incorrect items.
                    </p>
                    <p>
                      For certain categories like shoes and clothing, QC is especially important. Small details like logo placement, tag accuracy, and material texture can make a significant difference in product quality. Take the time to carefully review QC photos before approving your shipment.
                    </p>
                  </section>

                  <section id="shipping">
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                      {t('shipping_cost')}
                    </h2>
                    <p>
                      Shipping costs with CSSBuy depend on several factors: the weight and dimensions of your package, the destination country, and the shipping method you choose. Popular shipping methods include EMS, DHL, FedEx, and various postal services. Each method has different cost and speed trade-offs.
                    </p>
                    <p>
                      Volumetric weight may apply to some packages, especially large but lightweight items. This means that the shipping cost is calculated based on the package size rather than its actual weight. Understanding how volumetric weight works can help you optimize your shipping costs by consolidating items or choosing more efficient packaging.
                    </p>
                    <p>
                      For buyers in Brazil, shipping options may include EMS and postal services. Be aware that Brazilian customs may charge import duties and taxes on incoming packages. It is recommended to check the current customs regulations and duty thresholds before placing an order. For more details, see our <button onClick={() => navigate('/cssbuy-shipping-guide')} className="text-primary-600 hover:underline">Shipping Cost Guide</button>.
                    </p>
                  </section>

                  <section id="safety">
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                      {t('safety')}
                    </h2>
                    <p>
                      Is CSSBuy safe? The answer depends on how you use the platform and what precautions you take. CSSBuy as a platform has been operating for several years and provides standard agent services. However, there are always risks associated with international purchasing, including seller reliability, shipping delays, and customs issues.
                    </p>
                    <p>
                      To minimize risks, always use the QC photo service, verify seller information, choose secure payment methods, and start with smaller orders to test the process. Read the platform's policies on returns, refunds, and disputes before placing any orders.
                    </p>
                    <p>
                      Be cautious of deals that seem too good to be true, as they often are. If a seller is offering products at significantly lower prices than others, there may be quality issues or other problems. Always prioritize safety over savings, especially when making your first purchases. Read our full <button onClick={() => navigate('/cssbuy-safety-guide')} className="text-primary-600 hover:underline">Safety Guide</button> for more detailed advice.
                    </p>
                  </section>

                  <section id="comparison">
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
                      {t('comparison')}
                    </h2>
                    <p>
                      CSSBuy is not the only agent service available. Other popular options include Superbuy, Sugargoo, Oopbuy, and AllChinaBuy. Each platform has its own strengths and weaknesses in terms of pricing, shipping options, user interface, and customer service.
                    </p>
                    <div className="overflow-x-auto my-4">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-background-100">
                            <th className="text-left p-3 font-semibold border border-background-200">Feature</th>
                            <th className="text-left p-3 font-semibold border border-background-200">CSSBuy</th>
                            <th className="text-left p-3 font-semibold border border-background-200">Alternatives</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="p-3 border border-background-200">Product Research</td>
                            <td className="p-3 border border-background-200">Available via Spreadsheet</td>
                            <td className="p-3 border border-background-200">Varies by platform</td>
                          </tr>
                          <tr className="bg-background-50">
                            <td className="p-3 border border-background-200">QC Photos</td>
                            <td className="p-3 border border-background-200">Available</td>
                            <td className="p-3 border border-background-200">Available</td>
                          </tr>
                          <tr>
                            <td className="p-3 border border-background-200">Warehouse Storage</td>
                            <td className="p-3 border border-background-200">Available</td>
                            <td className="p-3 border border-background-200">Available</td>
                          </tr>
                          <tr className="bg-background-50">
                            <td className="p-3 border border-background-200">Shipping Options</td>
                            <td className="p-3 border border-background-200">Multiple</td>
                            <td className="p-3 border border-background-200">Multiple</td>
                          </tr>
                          <tr>
                            <td className="p-3 border border-background-200">Brazil Shipping</td>
                            <td className="p-3 border border-background-200">Available</td>
                            <td className="p-3 border border-background-200">Varies</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p>
                      The best choice depends on your specific needs, location, and the types of products you are buying. CSSBuy Spreadsheet is particularly useful for buyers who want organized product research before making purchasing decisions.
                    </p>
                  </section>

                  <section id="faq" className="bg-background-100 rounded-xl p-6 md:p-8">
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-6">
                      {t('faq')}
                    </h2>
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-medium text-foreground-900 mb-1">What is CSSBuy Spreadsheet?</h3>
                        <p className="text-sm">An independent product discovery and comparison resource organized by category with updated product information and seller details.</p>
                      </div>
                      <div>
                        <h3 className="font-medium text-foreground-900 mb-1">Is CSSBuy Spreadsheet free?</h3>
                        <p className="text-sm">Yes, the spreadsheet resource is free to use for browsing and research purposes.</p>
                      </div>
                      <div>
                        <h3 className="font-medium text-foreground-900 mb-1">How often is the spreadsheet updated?</h3>
                        <p className="text-sm">The spreadsheet is updated regularly with new product listings, price changes, and seller information.</p>
                      </div>
                      <div>
                        <h3 className="font-medium text-foreground-900 mb-1">Can I trust the sellers listed?</h3>
                        <p className="text-sm">While we include seller information, you should always verify on the platform and do your own research before purchasing.</p>
                      </div>
                    </div>
                  </section>
                </div>

                <div className="mt-12 pt-8 border-t border-background-200">
                  <div className="bg-primary-50 rounded-xl p-6 md:p-8 text-center">
                    <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">
                      {t('final_cta')}
                    </h3>
                    <p className="text-sm text-foreground-600 mb-6">
                      Start browsing products and comparing options on the CSSBuy Spreadsheet.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <a
                        href="https://kaspreadsheet.com"
                        target="_blank"
                        rel="noopener nofollow"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-medium rounded-lg hover:bg-primary-600 transition-colors text-sm whitespace-nowrap"
                      >
                        <i className="ri-external-link-line" />
                        {t('explore_spreadsheet')}
                      </a>
                      <button
                        onClick={() => navigate('/cssbuy-shipping-guide')}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-white text-foreground-700 font-medium rounded-lg border border-background-200 hover:bg-background-100 transition-colors text-sm whitespace-nowrap"
                      >
                        <i className="ri-arrow-right-line" />
                        {t('view_shipping_guide')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}